<template>
  <PageShell active="about">
    <section class="about-page">
      <aside class="about-nav" aria-label="About us sections">
        <div class="breadcrumb">
          <i class="el-icon-s-home"></i>
          <i class="el-icon-right"></i>
          <span>About us</span>
        </div>
        <a href="#about-us" @click.prevent="scrollTo('about-us')">About us</a>
        <a href="#warehouse-locations" @click.prevent="scrollTo('warehouse-locations')">Warehouse Locations</a>
        <a v-for="(warehouse, index) in warehouses" :key="warehouse.id || index" class="warehouse-link"
          :href="`#${warehouseAnchor(warehouse, index)}`"
          @click.prevent="scrollTo(warehouseAnchor(warehouse, index))">{{ warehouse.title }}</a>
        <a href="#contact-us" @click.prevent="scrollTo('contact-us')">Contact us</a>
      </aside>

      <div class="about-content">
        <section id="about-us" class="article-section">
          <h1>About Us</h1>
          <div v-loading="aboutLoading" class="article-content" v-html="aboutArticle.content"></div>
        </section>

        <section id="warehouse-locations" class="article-section warehouse-section">
          <h2>Warehouse Locations</h2>
          <div v-loading="warehouseLoading" class="warehouse-list">
            <article v-for="(warehouse, index) in warehouses" :id="warehouseAnchor(warehouse, index)"
              :key="warehouse.id || index" class="warehouse-item">
              <h3>{{ warehouse.title }}</h3>
              <div class="article-content" v-html="warehouse.content"></div>
            </article>
          </div>
        </section>

        <section id="contact-us" class="contact-section">
          <h2>Contact us</h2>
          <button type="button" @click="$router.push({ name: 'contact' })">Send message</button>
        </section>
      </div>
    </section>
  </PageShell>
</template>

<script>
import PageShell from '@/components/common/PageShell.vue'

export default {
  name: 'AboutPage',
  components: { PageShell },
  data() {
    return {
      aboutArticle: {},
      warehouses: [],
      aboutLoading: false,
      warehouseLoading: false
    }
  },
  mounted() {
    this.loadArticle('aboutArticle', 'aboutLoading', 1)
    this.loadWarehouses()
  },
  watch: {
    // 已在 About 页时切换 query.warehouse 也需重新定位锚点
    '$route.query.warehouse'(val) {
      if (val && this.warehouses.length) {
        this.$nextTick(() => this.scrollToWarehouseFromQuery())
      }
    }
  },
  methods: {
    async loadArticle(articleKey, loadingKey, id) {
      this[loadingKey] = true
      try {
        const res = await this.$api({
          url: 'Content/articleDetail',
          method: 'get',
          data: { id }
        })
        this[articleKey] = res.data || {}
      } catch (e) {
        this[articleKey] = {}
      } finally {
        this[loadingKey] = false
      }
    },
    async loadWarehouses() {
      this.warehouseLoading = true
      try {
        const res = await this.$api({
          url: 'Content/article',
          method: 'get',
          data: { category: 'warehouse' }
        })
        this.warehouses = Array.isArray(res.data) ? res.data : []
        this.scrollToWarehouseFromQuery()
      } catch (e) {
        this.warehouses = []
      } finally {
        this.warehouseLoading = false
      }
    },
    scrollToWarehouseFromQuery() {
      const targetName = this.$route.query.warehouse
      if (!targetName) return
      const index = this.warehouses.findIndex((w) => w.title === targetName)
      if (index >= 0) {
        this.$nextTick(() => {
          this.scrollTo(this.warehouseAnchor(this.warehouses[index], index))
        })
      }
    },
    warehouseAnchor(warehouse, index) {
      return `warehouse-${warehouse.id || index}`
    },
    scrollTo(id) {
      const target = document.getElementById(id)
      if (target) target.scrollIntoView({ behavior: 'smooth', block: 'start' })
    }
  }
}
</script>

<style scoped>
.about-page {
  max-width: 1100px;
  margin: 0 auto;
  padding: 14px 0 42px;
  /* display: grid; */
  grid-template-columns: 150px minmax(0, 1fr);
  gap: 0;
}

.about-nav {
  padding-top: 1px;
  color: #333;
  font-size: 14px;
  line-height: 1.35;
}

.breadcrumb {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 14px;
  font-size: 12px;
}

.breadcrumb :first-child {
  color: #f34b01;
}

.about-nav a,
.about-nav button {
  display: block;
  width: fit-content;
  margin: 0 0 12px 26px;
  padding: 0;
  border: 0;
  background: transparent;
  color: #f34b01;
  cursor: pointer;
  font-size: 14px;
  line-height: 1.35;
  text-align: left;
}

.about-nav a:nth-of-type(2) {
  color: #333;
}

.about-nav .warehouse-link {
  margin-left: 48px;
}

.about-nav button {
  margin-top: 2px;
}

.about-content {
  max-width: 1100px;
}

.article-section h1,
.article-section h2,
.contact-section h2 {
  margin: 0;
  color: #171717;
  font-size: 28px;
  font-weight: 400;
  line-height: 1.2;
}

.article-content {
  min-height: 22px;
  margin-top: 42px;
  color: #222;
  font-size: 16px;
  line-height: 1.48;
  white-space: normal;
}

.article-content ::v-deep p {
  margin: 0 0 22px;
}

.article-content ::v-deep h1,
.article-content ::v-deep h2,
.article-content ::v-deep h3 {
  margin: 0 0 28px;
  color: #222;
  font-size: 18px;
  font-weight: 400;
  line-height: 1.4;
}

.article-content ::v-deep img {
  max-width: 100%;
  height: auto;
}

.warehouse-section {
  margin-top: 52px;
}

.warehouse-list {
  min-height: 22px;
  margin-top: 38px;
}

.warehouse-item {
  scroll-margin-top: 20px;
}

.warehouse-item+.warehouse-item {
  margin-top: 44px;
}

.warehouse-item h3 {
  margin: 0;
  color: #222;
  font-size: 18px;
  font-weight: 400;
  line-height: 1.4;
}

.warehouse-item .article-content {
  margin-top: 34px;
}

.contact-section {
  margin-top: 72px;
}

.contact-section button {
  margin-top: 38px;
  padding: 0;
  border: 0;
  background: transparent;
  color: #f34b01;
  cursor: pointer;
  font-size: 18px;
  line-height: 1.3;
}

@media (max-width: 700px) {
  .about-page {
    display: block;
    min-height: auto;
    padding-top: 24px;
  }

  .about-nav {
    display: none;
  }

  .article-section h1,
  .article-section h2,
  .contact-section h2 {
    font-size: 25px;
  }

  .article-content {
    margin-top: 26px;
    font-size: 15px;
  }

  .warehouse-section,
  .contact-section {
    margin-top: 48px;
  }
}
</style>
